import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuditTable = ({ data, onSort, sortConfig, onRowExpand, expandedRows }) => {
  const [selectedRows, setSelectedRows] = useState(new Set());

  const getStatusBadge = (status) => {
    const statusConfig = {
      success: { color: 'bg-green-100 text-green-800', icon: 'CheckCircle', label: 'Exitoso' },
      error: { color: 'bg-red-100 text-red-800', icon: 'XCircle', label: 'Error' },
      timeout: { color: 'bg-yellow-100 text-yellow-800', icon: 'Clock', label: 'Tiempo agotado' },
      invalid: { color: 'bg-gray-100 text-gray-800', icon: 'AlertCircle', label: 'VIN inválido' }
    };

    const config = statusConfig?.[status] || statusConfig?.error;
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        <span>{config?.label}</span>
      </span>
    );
  };

  const getLatencyColor = (latency) => {
    if (latency < 1000) return 'text-green-600';
    if (latency < 3000) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleSort = (column) => {
    onSort(column);
  };

  const handleRowSelect = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected?.has(id)) {
      newSelected?.delete(id);
    } else {
      newSelected?.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedRows?.size === data?.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(data.map(row => row.id)));
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })?.format(new Date(date));
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Table Header Actions */}
      {selectedRows?.size > 0 && (
        <div className="bg-muted px-6 py-3 border-b border-border">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {selectedRows?.size} registros seleccionados
            </span>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
                iconSize={16}
              >
                Exportar seleccionados
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                iconSize={16}
                onClick={() => setSelectedRows(new Set())}
              />
            </div>
          </div>
        </div>
      )}
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="w-12 px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedRows?.size === data?.length && data?.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('timestamp')}
                  className="flex items-center space-x-1 hover:text-foreground transition-smooth"
                >
                  <span>Fecha y Hora</span>
                  <Icon name={getSortIcon('timestamp')} size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('user')}
                  className="flex items-center space-x-1 hover:text-foreground transition-smooth"
                >
                  <span>Usuario</span>
                  <Icon name={getSortIcon('user')} size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                VIN
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Vehículo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('latency')}
                  className="flex items-center space-x-1 hover:text-foreground transition-smooth"
                >
                  <span>Latencia</span>
                  <Icon name={getSortIcon('latency')} size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Ubicación
              </th>
              <th className="w-12 px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {data?.map((row) => (
              <React.Fragment key={row?.id}>
                <tr className="hover:bg-muted/50 transition-smooth">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedRows?.has(row?.id)}
                      onChange={() => handleRowSelect(row?.id)}
                      className="rounded border-border"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                    {formatDate(row?.timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="User" size={14} color="white" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground">{row?.user?.name}</div>
                        <div className="text-xs text-muted-foreground">{row?.user?.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-mono text-sm text-foreground">{row?.vin}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-foreground">{row?.vehicle?.brand} {row?.vehicle?.model}</div>
                      <div className="text-xs text-muted-foreground">{row?.vehicle?.year} • {row?.vehicle?.version}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getLatencyColor(row?.latency)}`}>
                      {row?.latency}ms
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(row?.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Icon name="MapPin" size={14} className="text-muted-foreground" />
                      <div>
                        <div className="text-sm text-foreground">{row?.location?.city}</div>
                        <div className="text-xs text-muted-foreground">{row?.location?.ip}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRowExpand(row?.id)}
                      iconName={expandedRows?.has(row?.id) ? "ChevronUp" : "ChevronDown"}
                      iconSize={16}
                    />
                  </td>
                </tr>
                
                {/* Expanded Row Details */}
                {expandedRows?.has(row?.id) && (
                  <tr>
                    <td colSpan="9" className="px-6 py-4 bg-muted/30">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                        <div>
                          <h4 className="font-medium text-foreground mb-2">Información de Sesión</h4>
                          <div className="space-y-1 text-muted-foreground">
                            <div>ID de Sesión: {row?.sessionId}</div>
                            <div>Dispositivo: {row?.device}</div>
                            <div>Navegador: {row?.browser}</div>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-foreground mb-2">Detalles Técnicos</h4>
                          <div className="space-y-1 text-muted-foreground">
                            <div>Tiempo de respuesta: {row?.responseTime}ms</div>
                            <div>Fuente de datos: {row?.dataSource}</div>
                            <div>Versión API: {row?.apiVersion}</div>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-foreground mb-2">Cumplimiento</h4>
                          <div className="space-y-1 text-muted-foreground">
                            <div>Geofencing: {row?.geofencing ? 'Cumple' : 'No cumple'}</div>
                            <div>Límite diario: {row?.dailyUsage}/50</div>
                            <div>Autenticación: {row?.authMethod}</div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card Layout */}
      <div className="lg:hidden divide-y divide-border">
        {data?.map((row) => (
          <div key={row?.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedRows?.has(row?.id)}
                  onChange={() => handleRowSelect(row?.id)}
                  className="rounded border-border mt-1"
                />
                <div>
                  <div className="font-medium text-foreground">{row?.user?.name}</div>
                  <div className="text-sm text-muted-foreground">{formatDate(row?.timestamp)}</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {getStatusBadge(row?.status)}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRowExpand(row?.id)}
                  iconName={expandedRows?.has(row?.id) ? "ChevronUp" : "ChevronDown"}
                  iconSize={16}
                />
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">VIN:</span>
                <span className="font-mono text-foreground">{row?.vin}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Vehículo:</span>
                <span className="text-foreground">{row?.vehicle?.brand} {row?.vehicle?.model}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Latencia:</span>
                <span className={`font-medium ${getLatencyColor(row?.latency)}`}>{row?.latency}ms</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Ubicación:</span>
                <span className="text-foreground">{row?.location?.city}</span>
              </div>
            </div>

            {/* Mobile Expanded Details */}
            {expandedRows?.has(row?.id) && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="space-y-3 text-sm">
                  <div>
                    <h4 className="font-medium text-foreground mb-1">Sesión</h4>
                    <div className="text-muted-foreground">
                      ID: {row?.sessionId}<br />
                      Dispositivo: {row?.device}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-1">Técnico</h4>
                    <div className="text-muted-foreground">
                      Respuesta: {row?.responseTime}ms<br />
                      API: {row?.apiVersion}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AuditTable;